import findArrayIndex from '@functions/utils/findArrayIndex'
import type { FindOptions } from '@functions/utils/findArrayIndex'

/**
 * Toggles a value's presence in an array. If the value exists, it is removed.
 * If `useObjectKey` is provided, the matched element is replaced.
 * If it does not exist, it is added. Handles both primitives and objects.
 *
 * @template T The type of the elements in the array (e.g., string, number, or a custom interface).
 * @param array The array to modify (must contain elements of type T).
 * @param value The element to toggle (must be of type T).
 * @param options Deep compare or key to use for object comparison/replacement.
 * @returns A new array (Array<T>) with the value toggled.
 */

function toggleArray<T>(
  array: T[],
  value: T, 
  options: FindOptions = {}
): T[] {
  const { useObjectKey } = options

  const sourceArray = array || []
  const updatedArray: T[] = [...sourceArray]

  const index = findArrayIndex(updatedArray, value, options)

  if (index !== -1) {
    if (useObjectKey) {
      updatedArray.splice(index, 1, value)
      return updatedArray
    }

    updatedArray.splice(index, 1)
  } else {
    updatedArray.push(value)
  }

  return updatedArray
}

export default toggleArray
